import { useQuery } from "@apollo/client/react";
import type { OutputOf } from "@gqlb/core";
import { Check, ChevronRight } from "lucide-react";
import type React from "react";
import { b } from "@/b";
import { CurrencyRenderer } from "@/components/CurrencyRenderer";
import type { SelectionState } from "@/graphql/fragments";
import { OPTION_SELECTOR_FRAGMENT, type SUB_OPTION_SELECTOR_FRAGMENT } from "@/graphql/fragments";

export { SUB_OPTION_SELECTOR_FRAGMENT } from "@/graphql/fragments";

import { OptionSelectorNew } from "./OptionSelectorNew";

const SUB_OPTION_QUERY_NEW = b.query(
  "SubOptionNew",
  { productId: "ID!", optionId: "String!" },
  (b, v) => [
    b.product({ selector: { id: v.productId } }, (b) => [
      b.id(),
      b.option({ id: v.optionId }, (b) => [b.__fragment(OPTION_SELECTOR_FRAGMENT)]),
    ]),
  ],
);

export const SubOptionSelectorNew: React.FC<{
  productId: string;
  optionId: string;
  data: OutputOf<typeof SUB_OPTION_SELECTOR_FRAGMENT>;
  selections: SelectionState[string];
  onSelectionUpdate: (
    selectionUpdateFn: (selectionState: SelectionState[string]) => SelectionState[string],
  ) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}> = ({ productId, optionId, data, selections, onSelectionUpdate, isOpen, setIsOpen }) => {
  const subOptionQuery = useQuery(SUB_OPTION_QUERY_NEW.document(), {
    variables: { productId, optionId },
  });

  const isSelected = selections.subOptions[data.id] !== undefined;
  const isSingle = data.minSelections === 1 && data.maxSelections === 1;

  return (
    <div>
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className={`
          w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border
          text-left select-none cursor-pointer
          transition-all duration-200
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-mint/50 focus-visible:ring-offset-1
          ${
            isSelected
              ? "border-brand-mint/50 bg-brand-mint/10"
              : "border-gray-200 bg-white hover:bg-gray-50"
          }
        `}
      >
        {/* Selection indicator */}
        <span
          className={`
            flex items-center justify-center size-5 border-2 shrink-0
            transition-all duration-150 ease-in-out
            ${isSingle ? "rounded-full" : "rounded-[4px]"}
            ${isSelected ? "border-brand-mint bg-brand-mint" : "border-gray-400 bg-white"}
          `}
        >
          <span
            className={`transition-transform duration-150 ${isSelected ? "scale-100" : "scale-0"}`}
          >
            {isSingle ? (
              <span className="block size-2 rounded-full bg-brand-black" />
            ) : (
              <Check className="size-3 text-brand-black" strokeWidth={3} />
            )}
          </span>
        </span>

        {/* Chevron */}
        <ChevronRight
          className={`size-4 text-gray-400 shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-90" : ""
          }`}
        />

        {/* Name */}
        <span className={`flex-1 text-sm text-brand-black ${isSelected ? "font-semibold" : ""}`}>
          {data.name}
        </span>

        {/* Price */}
        <span className="text-sm text-gray-500 shrink-0 tabular-nums">
          <CurrencyRenderer data={data.price} freeStyle="null" />
        </span>
      </button>

      {/* Collapsible content */}
      {subOptionQuery.data?.product?.option && (
        <div
          className="grid transition-[grid-template-rows] duration-300 ease-out"
          style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <div className="pl-7 pt-2 pb-1 relative">
              <div className="absolute top-0 left-3 bottom-0 w-px bg-gray-200" />
              <OptionSelectorNew
                data={subOptionQuery.data.product.option}
                selections={
                  selections.subOptions[data.id] ?? {
                    selections: new Set(),
                    subOptions: {},
                    order: [],
                  }
                }
                onSelectionUpdate={(stateUpdateFn) => {
                  onSelectionUpdate((prev) => {
                    const currentState = prev.subOptions[data.id] ?? {
                      selections: new Set(),
                      subOptions: {},
                      order: [],
                    };
                    const newOptionState = stateUpdateFn(currentState);

                    if (
                      newOptionState.selections.size === 0 &&
                      Object.keys(newOptionState.subOptions).length === 0
                    ) {
                      const subOptions = { ...prev.subOptions };
                      delete subOptions[data.id];
                      const order = prev.order.filter((id) => id !== data.id);
                      return { ...prev, subOptions, order };
                    }

                    return {
                      ...prev,
                      subOptions: {
                        ...prev.subOptions,
                        [data.id]: newOptionState,
                      },
                      order: prev.order.filter((id) => id !== data.id).concat(data.id),
                    };
                  });
                }}
                productId={productId}
                showTitle={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
